/**
 * ## Application.tsx ##
 * This file contains Application component
 * @packageDocumentation
 */

import * as React from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { bindActionCreators } from 'redux';

import once from 'lodash/once';
import isEmpty from 'lodash/isEmpty';

import Cookies from 'js-cookie';

import { BaseUser } from '@common/typescript/objects/BaseUser';
import { Lang } from '@common/typescript/objects/Lang';
import { Notification } from '@common/typescript/objects/Notification';
import { BaseApplicationState } from '@common/react/store';
import BaseHostOptions from '@common/react/store/BaseHostOptions';
import { getActionCreators } from '@common/react/store/Login';
import { getSignalR } from '@common/react/utils/configureSignarR';
import useRequest from '@common/react/hooks/useRequest';
import { BuildData } from '@common/react/objects/BuildData';
import { BaseInit } from '@common/react/objects/BaseInit';
import { RequestType } from '@common/react/components/RequestProvider/RequestProvider';

/**
 * used for checkUserAccess method
 *
 * @param roles - allowed roles
 * @param props - other option
 * @return boolean - return true if user have such role
 */
export type CheckUserAccessDelegate = (roles: Array<number>, props?: any) => boolean;

export interface ApplicationContext {
	/**
	 * callback after transmutation
	 */
	onTransmutation: () => void;
	/**
	 * hook to get transmutation flag
	 */
	getTransmutationFlag: () => boolean;
	/**
	 * hook to get current User
	 */
	getUser: <TUser extends BaseUser>() => TUser;
	/**
	 * function to update current user
	 */
	updateUser: <TUser extends BaseUser>(data: any, getUser?: (user: TUser) => any) => void;
	/**
	 * hook to get Host Options
	 */
	getHostOptions: <THostOptions extends BaseHostOptions>() => THostOptions;
	/**
	 * hook to get Build Data
	 */
	getBuildData: <TBuildData extends BuildData>() => TBuildData;
	/**
	 * hook to get Lang
	 */
	getLang: () => Lang;
	/**
	 * function to update lang
	 * @param lang - new lang
	 */
	setLang: (lang: Lang) => void;
	/**
	 * custom request
	 * @param type - string,
	 * @param data - request data
	 * @param options - request options
	 * @return Promise<any>
	 */
	request: RequestType;
	/**
	 * function for subscribing to notifications
	 *
	 * usage React.useEffect(subscribe(handler), deps);
	 *
	 * @param handler - handler function
	 */
	subscribe: (handle: (notify: any) => void) => () => void;
	/**
	 * function to check user role
	 */
	checkUserAccess: CheckUserAccessDelegate;
	/**
	 * hook like useEffect when user is defined
	 *
	 * @param factory
	 * @param deps - React.DependencyList
	 * @return boolean
	 */
	memoUntilLogout: <T>(factory: () => T, deps?: React.DependencyList | undefined) => T;
	/**
	 * hook for subscribing to notifications
	 *
	 * @param handler - handler function
	 */
	subscribeUntilLogout: <T extends Notification<BaseUser>>(handle: (notify: T) => void, deps?: React.DependencyList | undefined) => void;
	/**
	 * callback after login
	 *
	 * @param init - init object
	 */
	handleLogin: <TUser extends BaseUser, TInit extends BaseInit<TUser>>(init: TInit) => void;
	/**
	 * callback after logout
	 *
	 * @param clearState - need clear state or not
	 */
	logout: (clearState?: boolean) => void;
	/**
	 * hook to get user utc offset
	 */
	getUserUtcOffset: () => string | undefined;
}

/**
 * This is the description of the interface
 *
 * @interface ApplicationProps
 * @typeParam TUser - TUser Any BaseUser entity
 * @typeParam TInit - TInit Any BaseInit<TUser> entity
 */
interface ApplicationProps<TUser extends BaseUser, TInit extends BaseInit<TUser>> {
	/**
	 * - 1. ReactElement to be wrapped in an Application context
	 * - 2. function with context Application as first argument
	 */
	children?: React.ReactNode | ((actions: ApplicationContext) => React.ReactNode);
	/**
	 * add - used when add from a context is called without arguments. Sets default values for a new element
	 * @param dispatch - update store function
	 * @param init - init object
	 */
	initCustomHandler?: ((dispatch: any, init: TInit) => void);
	/**
	 * callback after transmutation
	 */
	transmutationHandler?: () => void;
	/**
	 * custom user access function
	 * @param user - current user
	 * @param roles - allowed roles
	 * @param props - other params
	 */
	checkUserAccess?: (user: TUser, roles: Array<number>, props?: any) => boolean;
	/**
	 * add SignalR or not
	 * must be constant (affects the number of hooks)
	 */
	withoutSignalR?: boolean;
}

export const subscribe = (handle: (notify: any) => void) => () => {
	getSignalR().then((connection) => connection.on('handle', handle));

	return () => {
		getSignalR().then((connection) => connection.off('handle', handle));
	};
};

export const createApplicationContext = once(() => React.createContext({} as ApplicationContext));

export const useApplicationContext: () => ApplicationContext = () => {
	const context: ApplicationContext = React.useContext(createApplicationContext());

	if (isEmpty(context)) throw 'Need Application context!';

	return context;
};

/**
 * Application component.
 *
 * @example
 * <Application<TUser, TInit>>{React.ReactNode}</Application>
 *
 * @example
 * <Application<TUser, TInit>>{(context) => React.ReactNode}</Application>
 *
 * @typeParam TUser - TUser Any {@link BaseUser}
 * @typeParam TInit - TInit Any {@link BaseInit}
 * @param props - {@link ApplicationProps}
 * @returns React.ReactElement
 */
const Application: <TUser extends BaseUser, TInit extends BaseInit<TUser>>(
	p: ApplicationProps<TUser, TInit>,
) => React.ReactElement<TUser> = <TUser extends BaseUser, TInit extends BaseInit<TUser>>(props: ApplicationProps<TUser, TInit>) => {
	const {
		children,
		initCustomHandler,
		transmutationHandler,
		checkUserAccess,
		withoutSignalR,
	} = props;
	const ApplicationContext = createApplicationContext();

	const onTransmutation = transmutationHandler ?? (() => window.location.replace('/dashboard'));
	const getUser = <TUser extends BaseUser>() =>
		useSelector((state: BaseApplicationState<TUser>) => state.login.user! as TUser, shallowEqual);
	const getHostOptions = <THostOptions extends BaseHostOptions>() =>
		useSelector((state: any) => (state.hostOptions.item), shallowEqual) as THostOptions;
	const getBuildData = <TBuildData extends BuildData>() =>
		useSelector((state: any) => (state.buildData.item), shallowEqual) as TBuildData;
	const getTransmutationFlag = <TUser extends BaseUser>() =>
		useSelector((state: BaseApplicationState<TUser>) => state.login.transmuted, shallowEqual);
	const getLang: () => Lang = () => useSelector((state: BaseApplicationState<TUser>) => state.login.lang, shallowEqual);
	const request = useRequest();
	const getUserUtcOffset = () => getUser<BaseUser & { baseUtcOffset?: string }>().baseUtcOffset || undefined;

	const user = getUser() as TUser;
	const _checkUserAccess = (roles: Array<number>, props: any) => (checkUserAccess && checkUserAccess(user!, roles, props)) || false;
	const dispatch = useDispatch();

	const {
		logoff,
		updateUser,
		setUserAndSession,
		setLang,
	} = React.useMemo(() => bindActionCreators(getActionCreators(), dispatch), [user]);

	const logout = (clearState?: boolean) => logoff(clearState);

	const memoUntilLogout = (factory: () => unknown, deps?: React.DependencyList | undefined) =>
		React.useMemo(factory, deps ? [user].concat(deps) : [user]);

	const subscribeUntilLogout = (handler: (notify: Notification<TUser>) => void, deps?: React.DependencyList | undefined) =>
		React.useEffect(subscribe(handler), deps ? [user].concat(deps) : [user]);

	const handleSession = <TInit extends BaseInit<TUser>>(data: TInit) => {
		const sessionGuid = data.guid as string;
		// It is necessary to put a cookie on the frontend at the time of receiving a notification
		// if the connection to the signalR has been lost (when login)
		Cookies.set('session', sessionGuid);
		setUserAndSession(data.user as BaseUser, sessionGuid);
	};

	const handleLogin = (init: TInit) => {
		if (init && init.user && init.guid) {
			!user && initCustomHandler && initCustomHandler(dispatch, init);
			handleSession(init);
		}
	};

	const baseHandler = (notification: Notification<TUser>) => {
		switch (notification.objectType) {
			case 'Init':
				// it is necessary to reload all pages of the user if he transmuted into another
				if (user && notification.data?.user?.id !== user.id) {
					onTransmutation();
					break;
				}
				handleLogin(notification.data);
				break;
			case 'UserSession':
				handleSession(notification.data);
				break;
			default: {
				break;
			}
		}
	};

	const handle = (notification: Notification<TUser>) => {
		switch (notification.objectType) {
			case 'NotificationUser':
				if (user && notification.service && user.unviewedNotificationsCount > 0) {
					updateUser({ unviewedNotificationsCount: user.unviewedNotificationsCount - 1 });
				}
				break;
			case 'ReadAllNotification':
				if (notification.service) {
					updateUser({ unviewedNotificationsCount: 0 });
				}
				break;
			case 'UserStateNotificationObject':
				if (user && notification.service) {
					const status = notification.data.userStatuses.find((q) => q.id === user.id)?.status;
					status && updateUser({ status: status.status });
				}
				break;
			default:
				if (user && !notification.service) {
					updateUser({ unviewedNotificationsCount: user.unviewedNotificationsCount + 1 });
				}
				break;
		}
	};

	if (!withoutSignalR) {
		React.useEffect(subscribe(baseHandler), [!user]);
		subscribeUntilLogout(handle);
	}

	const actions = memoUntilLogout(() => ({
		onTransmutation,
		getTransmutationFlag,
		getUser,
		getHostOptions,
		getBuildData,
		getLang,
		setLang,
		request,
		subscribe,
		updateUser,
		memoUntilLogout,
		subscribeUntilLogout,
		handleLogin,
		checkUserAccess: _checkUserAccess,
		logout,
		getUserUtcOffset,
	})) as ApplicationContext;

	return <>
		<ApplicationContext.Provider value={actions}>
			{typeof children === 'function' ? children(actions) : children}
		</ApplicationContext.Provider>
	</>;
};

interface AfterLogoutProps {
	callback: () => void
}

export const AfterLogout: React.FC<AfterLogoutProps> = ({
	callback,
}) => {
	const { getUser } = useApplicationContext();
	const user = getUser();

	React.useEffect(() => {
		user == null && callback && callback();
	}, [user]);

	return <></>;
};

export default Application;
